import React from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import type { AutosaveStatus } from './useAutosave';

interface SaveStatusProps {
  status: AutosaveStatus;
  error?: string | null;
  lastSavedAt?: Date | null;
  hasUnsavedChanges: boolean;
  onSaveNow: () => void;
  /** The initial load failed, so saving is blocked. */
  blocked?: boolean;
}

/**
 * Autosave state in the studio header: "Saving…", "All changes saved",
 * "Couldn't save — Retry", with "Save now" as a manual fallback.
 */
const SaveStatus: React.FC<SaveStatusProps> = ({ status, error, lastSavedAt, hasUnsavedChanges, onSaveNow, blocked = false }) => {
  let icon: React.ReactNode;
  let text: string;
  let color = 'text.secondary';
  let action: React.ReactNode = null;

  if (blocked) {
    icon = <CloudOffOutlinedIcon fontSize="small" />;
    text = 'Saving is off until you reload';
    color = 'error.main';
  } else if (status === 'saving') {
    icon = <CircularProgress size={14} thickness={5} />;
    text = 'Saving…';
  } else if (status === 'error') {
    icon = <ErrorOutlineIcon fontSize="small" />;
    text = 'Couldn’t save';
    color = 'error.main';
    action = (
      <Button size="small" color="error" onClick={onSaveNow} sx={{ minWidth: 0, px: 1, py: 0.25 }}>
        Retry
      </Button>
    );
  } else if (hasUnsavedChanges) {
    icon = <EditNoteOutlinedIcon fontSize="small" />;
    text = status === 'pending' ? 'Unsaved changes' : 'Not saved yet';
    action = (
      <Button size="small" variant="outlined" onClick={onSaveNow} sx={{ minWidth: 0, px: 1.25, py: 0.25 }}>
        Save now
      </Button>
    );
  } else {
    icon = <CloudDoneOutlinedIcon fontSize="small" />;
    text = status === 'saved' || lastSavedAt ? 'All changes saved' : 'Changes save automatically';
  }

  const tooltip = status === 'error' && error
    ? error
    : lastSavedAt
      ? `Last saved ${lastSavedAt.toLocaleTimeString()}`
      : 'Notes save automatically a moment after each change';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="save-status">
      <Tooltip title={tooltip}>
        <Box
          role="status"
          aria-live="polite"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color, '& svg': { fontSize: 18 } }}
        >
          {icon}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>
            {text}
          </Typography>
        </Box>
      </Tooltip>
      {action}
    </Box>
  );
};

export default SaveStatus;
